/**
 * Delivery admin for one project (track B2, hub tab "delivery").
 *
 * Lists the project's scheduled deliveries; creating/editing happens in
 * `DeliveryWizard` (Plan F, F0) — a 4-step flow that speaks plain
 * language instead of raw cron and secret pointers. Three things this
 * surface is deliberately opinionated about:
 *
 * 1. **No credential — and since F0, no credential POINTER either — is
 *    ever typed here.** The wizard picks from the Integrations list;
 *    `secret_ref` survives only in the API for pre-033 rows.
 * 2. **Test before you trust.** "Test" runs the full resolution ladder
 *    (credential → content → locale → render) and sends NOTHING. A
 *    missing credential comes back as a loud 424 with the real reason,
 *    which is the whole point of the button.
 * 3. **The schedule column speaks human** ("Every Monday at 08:00") —
 *    the raw cron is in the tooltip, not the row.
 */

import { Badge, Button } from "@omnitok/ui";
import { DataTable, EmptyState } from "@pervasivemind/admin-ui";
import { Spinner } from "@pervasivemind/bi-components";
import {
  AlertCircle,
  Check,
  ChevronDown,
  ChevronRight,
  FlaskConical,
  Pencil,
  Plus,
  RefreshCw,
  Send,
  Trash2,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { useT } from "@/i18n";
import {
  deliveryApi,
  type DeliveryJob,
  type DeliverySubscription,
  type DeliveryTestResult,
} from "@/lib/builder-api";
import {
  browserTimezone,
  emptyForm,
  formFromSubscription,
  timezoneLabel,
  type FormState,
} from "@/pages/delivery/delivery-form";
import { DeliveryWizard } from "@/pages/delivery/DeliveryWizard";
import { summarizeCron } from "@/pages/delivery/schedule-summary";
import {
  integrationsApi,
  type ChannelCredential,
} from "@/lib/integrations-api";

export interface DeliverySubscriptionsPanelProps {
  projectSlug: string;
  /** The project's reports, already resolved to a display name. */
  reports: { key: string; name: string }[];
}

function statusVariant(
  status: string,
): "success" | "error" | "warning" | "neutral" {
  if (status === "sent") return "success";
  if (status === "failed" || status === "dead") return "error";
  if (status === "pending" || status === "running") return "warning";
  return "neutral";
}

/** A subscription's loaded history, tagged with the row that asked for it (#260). */
export interface OwnedJobs {
  subId: string;
  jobs: DeliveryJob[];
}
export type RowHistory = "hidden" | "loading" | DeliveryJob[];

export function rowHistory(
  subId: string,
  expanded: string | null,
  jobsFor: OwnedJobs | null,
): RowHistory {
  if (expanded !== subId) return "hidden";
  if (jobsFor === null || jobsFor.subId !== subId) return "loading";
  return jobsFor.jobs;
}

function formatTs(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso.endsWith("Z") ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function DeliverySubscriptionsPanel({
  projectSlug,
  reports,
}: DeliverySubscriptionsPanelProps): JSX.Element {
  const { t, locale } = useT();
  const [subs, setSubs] = useState<DeliverySubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [jobsFor, setJobsFor] = useState<OwnedJobs | null>(null);
  // The row whose history the latest click asked for — a slower,
  // earlier response must not land under a later row (#260).
  const requestedRef = useRef<string | null>(null);
  const [testResult, setTestResult] = useState<DeliveryTestResult | null>(
    null,
  );
  // Plan C 2.3: the reusable credentials this project can pick from.
  // Loaded once for the whole tenant — a credential is deliberately NOT
  // project-scoped, that is the entire point of the change.
  const [credentials, setCredentials] = useState<ChannelCredential[]>([]);

  const showFeedback = useCallback((msg: string): void => {
    setFeedback(msg);
    setTimeout(() => setFeedback(null), 2500);
  }, []);

  // Stale-while-revalidate (dashboard-render motion rule 9): the
  // full-panel spinner is FIRST LOAD only. A refetch — like the one the
  // wizard's onSaved triggers right after "Save & test" — must keep the
  // current DOM mounted: swapping to the spinner here unmounted the
  // wizard mid-flow and threw away its test result (seen live in
  // staging, 2026-08-07).
  const hasSettledOnce = useRef(false);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      setSubs(await deliveryApi.listForProject(projectSlug));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "error");
    } finally {
      hasSettledOnce.current = true;
      setLoading(false);
    }
  }, [projectSlug]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    // A failure here must not blank the panel: the legacy `secret_ref`
    // path still works, so the picker degrades to "no credentials
    // listed" and the reason is shown, rather than the whole delivery
    // screen dying because the integrations surface is unreachable.
    integrationsApi.channels
      .list(locale)
      .then((out) => setCredentials(out.credentials))
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : String(err)),
      );
  }, [locale]);

  const handleTest = async (id: string): Promise<void> => {
    setBusyId(id);
    setTestResult(null);
    try {
      setTestResult(await deliveryApi.test(id));
    } catch (err) {
      // A 424 here is the feature working: the real reason (missing
      // credential, stale content, absent translation) is surfaced.
      setError(err instanceof Error ? err.message : "error");
    } finally {
      setBusyId(null);
    }
  };

  const handleSendNow = async (id: string): Promise<void> => {
    if (!window.confirm(t("delivery.confirmSendNow"))) return;
    setBusyId(id);
    try {
      const res = await deliveryApi.sendNow(id);
      showFeedback(t("delivery.sendNowResult", { status: res.status }));
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "error");
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (sub: DeliverySubscription): Promise<void> => {
    if (!window.confirm(t("delivery.confirmDelete", { name: sub.name })))
      return;
    setBusyId(sub.id);
    try {
      await deliveryApi.remove(sub.id);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "error");
    } finally {
      setBusyId(null);
    }
  };

  const toggleJobs = async (id: string): Promise<void> => {
    if (expanded === id) {
      requestedRef.current = null;
      setExpanded(null);
      return;
    }
    requestedRef.current = id;
    setExpanded(id);
    try {
      const res = await deliveryApi.jobsForSubscription(id, 10);
      if (requestedRef.current !== id) return; // a later click owns the row now
      setJobsFor({ subId: id, jobs: res.jobs });
    } catch (err) {
      if (requestedRef.current !== id) return;
      requestedRef.current = null;
      setExpanded(null); // nothing true to show under this row; the banner says why
      setError(err instanceof Error ? err.message : "error");
    }
  };

  if (loading && !hasSettledOnce.current) {
    return (
      <div className="flex items-center gap-2 py-10 text-sm text-neutral-500">
        <Spinner size={16} /> {t("common.loading")}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h2 className="text-lg font-semibold text-neutral-900">
            {t("delivery.title")}
          </h2>
          <p className="mt-1 text-sm text-neutral-500">
            {t("delivery.subtitle")}
          </p>
        </div>
        <div className="ml-auto flex gap-2">
          <Button
            ghost
            size="sm"
            onClick={() => void load()}
            leftIcon={<RefreshCw size={14} />}
          >
            {t("common.refresh")}
          </Button>
          <Button
            size="sm"
            onClick={() =>
              // New subscriptions default to the admin's browser zone —
              // nobody should have to know an IANA name (F0).
              setForm(emptyForm(reports[0]?.key ?? "", browserTimezone()))
            }
            leftIcon={<Plus size={14} />}
          >
            {t("delivery.action.new")}
          </Button>
        </div>
      </div>

      {feedback && (
        <div className="flex items-center gap-2 rounded-lg border border-success/40 bg-success/10 px-4 py-2 text-sm text-success-dark">
          <Check size={14} /> {feedback}
        </div>
      )}
      {error && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-lg border border-error/40 bg-error/5 px-4 py-2 text-sm text-error-dark"
        >
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          <span className="whitespace-pre-wrap">{error}</span>
          <button
            type="button"
            onClick={() => setError(null)}
            className="ml-auto text-error-dark"
            aria-label={t("common.cancel")}
          >
            ✕
          </button>
        </div>
      )}

      {form && (
        <DeliveryWizard
          projectSlug={projectSlug}
          reports={reports}
          credentials={credentials}
          initial={form}
          onClose={() => setForm(null)}
          onSaved={() => {
            showFeedback(t("common.saved"));
            void load();
          }}
        />
      )}

      {testResult && (
        <div className="space-y-2 rounded-lg border border-neutral-200 bg-white p-4">
          <div className="flex items-center gap-2 text-sm font-medium text-neutral-800">
            <FlaskConical size={14} />
            {t("delivery.test.title", {
              date: testResult.as_of_date,
              locale: testResult.locale,
            })}
            <button
              type="button"
              className="ml-auto text-neutral-500"
              onClick={() => setTestResult(null)}
              aria-label={t("common.cancel")}
            >
              ✕
            </button>
          </div>
          <pre className="max-h-80 overflow-auto whitespace-pre-wrap rounded bg-neutral-50 p-3 text-xs text-neutral-700">
            {testResult.rendered_markdown}
          </pre>
        </div>
      )}

      {form ? null : (
        <DataTable
          rows={subs}
          rowKey={(sub) => sub.id}
          caption={t("delivery.title")}
          empty={
            <EmptyState
              icon={<Send size={20} />}
              title={t("delivery.empty")}
            />
          }
          // The chevron keeps its own narrow cell, as it had.
          leadingCell={(sub) => (
            <button
              type="button"
              onClick={() => void toggleJobs(sub.id)}
              aria-expanded={expanded === sub.id}
              aria-label={t("delivery.action.history")}
              className="rounded p-1 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800"
            >
              {expanded === sub.id ? (
                <ChevronDown size={14} />
              ) : (
                <ChevronRight size={14} />
              )}
            </button>
          )}
          expandedRow={(sub) => {
            const history = rowHistory(sub.id, expanded, jobsFor);
            if (history === "hidden") return null;
            if (history === "loading") {
              return <Spinner size={14} ariaLabel={t("common.loading")} />;
            }
            return history.length === 0 ? (
              <span className="text-xs text-neutral-500">
                {t("delivery.noJobs")}
              </span>
            ) : (
              <ul className="space-y-1 text-xs">
                {history.map((j) => (
                  <li key={j.id} className="flex gap-2">
                    <span
                      title={
                        j.status === "sent" ? t("delivery.sentMeaning") : undefined
                      }
                    >
                      <Badge variant={statusVariant(j.status)} size="sm">
                        {t(`delivery.status.${j.status}`)}
                      </Badge>
                    </span>
                    <span className="text-neutral-600">
                      {formatTs(j.scheduled_for)}
                    </span>
                    <span className="text-neutral-500">
                      {j.attempts}/{j.max_attempts}
                    </span>
                    {j.error_summary && (
                      <span className="text-error-dark">
                        [{j.error_kind}] {j.error_summary}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            );
          }}
          columns={[
            {
              id: "name",
              header: t("delivery.field.name"),
              cell: (sub) => (
                <span className="font-medium text-neutral-800">
                  {sub.name}
                  {!sub.enabled && (
                    <Badge variant="neutral" size="sm" className="ml-2">
                      {t("delivery.status.disabled")}
                    </Badge>
                  )}
                </span>
              ),
            },
            {
              id: "report",
              header: t("delivery.field.report"),
              cell: (sub) => (
                <span className="text-neutral-600">{sub.report_key}</span>
              ),
            },
            {
              id: "channel",
              header: t("delivery.field.channel"),
              cell: (sub) => (
                <Badge variant="neutral" size="sm">
                  {t(`delivery.channel.${sub.channel}`)}
                </Badge>
              ),
            },
            {
              id: "schedule",
              header: t("delivery.col.schedule"),
              // Human phrase; the raw cron survives in the title
              // attribute for whoever hovers (F0).
              cell: (sub) => (
                <span
                  className="text-neutral-600"
                  title={`${sub.cron} · ${sub.timezone}`}
                >
                  {summarizeCron(sub.cron, t)}
                  <span className="ml-1 text-xs text-neutral-500">
                    {timezoneLabel(sub.timezone, locale)}
                  </span>
                </span>
              ),
            },
            {
              id: "locale",
              header: t("delivery.field.locale"),
              cell: (sub) => (
                <span className="text-neutral-600">{sub.locale}</span>
              ),
            },
            {
              id: "nextRun",
              header: t("delivery.col.nextRun"),
              cell: (sub) => (
                <span className="text-neutral-600">
                  {formatTs(sub.next_run_at)}
                </span>
              ),
            },
            {
              id: "lastStatus",
              header: t("delivery.col.lastStatus"),
              cell: (sub) => (
                <>
                  {sub.last_status ? (
                    <span
                      title={
                        sub.last_status === "sent"
                          ? t("delivery.sentMeaning")
                          : undefined
                      }
                    >
                      <Badge variant={statusVariant(sub.last_status)} size="sm">
                        {t(`delivery.status.${sub.last_status}`)}
                      </Badge>
                    </span>
                  ) : (
                    <span className="text-neutral-500">—</span>
                  )}
                  {sub.consecutive_failures > 0 && (
                    <span className="ml-1 text-xs text-error-dark">
                      ×{sub.consecutive_failures}
                    </span>
                  )}
                </>
              ),
            },
          ]}
          // Four actions were four inline ghost buttons of equal weight,
          // and two ADJACENT ones were "Enviar ahora" — which really
          // mails the customer — and "Eliminar". Behind a kebab, named,
          // with the destructive one last and separated.
          actions={[
            {
              id: "test",
              label: t("delivery.action.test"),
              icon: <FlaskConical size={14} />,
              disabled: (sub) => busyId === sub.id,
              onSelect: (sub) => void handleTest(sub.id),
            },
            {
              id: "sendNow",
              label: t("delivery.action.sendNow"),
              icon: <Send size={14} />,
              disabled: (sub) => busyId === sub.id,
              onSelect: (sub) => void handleSendNow(sub.id),
            },
            {
              id: "edit",
              label: t("common.edit"),
              icon: <Pencil size={14} />,
              onSelect: (sub) => setForm(formFromSubscription(sub)),
            },
            {
              id: "delete",
              label: t("delivery.action.delete"),
              icon: <Trash2 size={14} />,
              danger: true,
              disabled: (sub) => busyId === sub.id,
              onSelect: (sub) => void handleDelete(sub),
            },
          ]}
        />
      )}

    </div>
  );
}

export default DeliverySubscriptionsPanel;
